import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Lock, Check, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';

function validatePassword(pwd: string, t: (k: string) => string): string | null {
  if (pwd.length < 8) return t('auth.changePassword.errMinLength');
  if (!/[A-Za-z]/.test(pwd)) return t('auth.changePassword.errLetter');
  if (!/[0-9]/.test(pwd)) return t('auth.changePassword.errNumber');
  return null;
}

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const logout = useAuth((s) => s.logout);
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const validation = newPwd ? validatePassword(newPwd, t) : null;
  const matches = newPwd && confirm && newPwd === confirm;
  const ready = oldPwd && newPwd && !validation && matches && newPwd !== oldPwd;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await apiClient.post('/api/v1/auth/me/change-password', { oldPassword: oldPwd, newPassword: newPwd });
      setDone(true);
      // Force re-login so JWT refreshes with mustChangePassword=false
      setTimeout(() => logout(), 1500);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen w-full bg-background flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md animate-fade-in">
        <div className="bg-card border border-border rounded-lg shadow-xl p-8">
          {done ? (
            <div className="space-y-4 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">
                <Check className="h-6 w-6" />
              </div>
              <h2 className="font-display text-2xl">{t('auth.changePassword.changed')}</h2>
              <p className="text-sm text-muted-foreground">{t('auth.changePassword.signingOut')}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div>
                <h2 className="font-display text-2xl">{t('auth.changePassword.title')}</h2>
                <p className="text-sm text-muted-foreground mt-1">{t('auth.changePassword.subtitle')}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="oldPwd">{t('auth.changePassword.current')}</Label>
                <div className="relative">
                  <Lock className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="oldPwd" type="password" autoComplete="current-password" autoFocus
                    value={oldPwd} onChange={(e) => setOldPwd(e.target.value)} className="ps-10" required />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPwd">{t('auth.changePassword.new')}</Label>
                <div className="relative">
                  <Lock className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="newPwd" type="password" autoComplete="new-password"
                    value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="ps-10" required />
                </div>
                {newPwd && validation && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">{validation}</p>
                )}
                {newPwd && !validation && newPwd === oldPwd && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">{t('auth.changePassword.errSameAsOld')}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm">{t('auth.changePassword.confirm')}</Label>
                <div className="relative">
                  <Lock className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="confirm" type="password" autoComplete="new-password"
                    value={confirm} onChange={(e) => setConfirm(e.target.value)} className="ps-10" required />
                </div>
                {confirm && !matches && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">{t('auth.changePassword.errMismatch')}</p>
                )}
              </div>

              {error && (
                <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm inline-flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" /> {error}
                </div>
              )}

              <Button type="submit" variant="gold" size="xl" className="w-full" disabled={submitting || !ready}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {t('auth.changePassword.submit')}
              </Button>

              <button type="button" onClick={() => navigate('/dashboard')} className="w-full text-xs text-muted-foreground hover:text-foreground">
                {t('auth.changePassword.skip')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
